<template>
  <div class="longread-detail-view page" :class="pageCollection">
    <div class="page__inner">
      <AppPageLoader :collection="pageCollection" @loaded="onPageLoaded">
        <AppLongreadHero
          :image="state.data.image"
          class="longread-detail-view__longread-hero"
        />

        <header class="longread-detail-view__longread-header">
          <div class="longread-detail-view__inner">
            <div
              class="longread-detail-view__content"
            >
              <h1
                class="longread-detail-view__title"
                v-html="state.data.title"
              />
            </div>
          </div>
        </header>

        <article class="longread-detail-view__article">
          <div class="longread-detail-view__wrapper">
            <div class="longread-detail-view__container">
              <small class="longread-detail-view__small">
                <component :is="state.update ? 'span' : 'time'" v-if="state.date" :datetime="!state.update ? state.data.date_created : null" class="longread-detail-view__component">{{ state.date }}</component>
                <span v-if="state.date"> - </span><span>{{ state.data.reading_time }} min lezen</span>
                <time v-if="state.update" class="longread-detail-view__time" :datetime="state.data?.date_updated">{{ state.update }}</time>
              </small>
              <AppRichText v-if="state.data.introduction" class="app-rich-text--introduction" :data="state.data.introduction" />
              <AppTagButtonList :data="state.data.belongs_to" class="longread-detail-view__tag-button-list" />
            </div>
          </div>
          <div class="longread-detail-view__content-wrapper">
            <AppDynamicBlock
              :is="useBlock(block)"
              v-for="block in state.data.content"
              :key="`block_${block.id}`"
              :data="block"
            />
          </div>
          <ClientOnly>
            <LayoutsShare v-show="computedChaptersOpened" v-if="state.data?.share" />
          </ClientOnly>
          <AppSterBanner v-show="computedChaptersOpened" />
          <AppGridBlockStatic
            v-show="computedChaptersOpened"
            v-if="state.data"
            variant="more"
          />
          <AppGridBlockStatic
            v-show="computedChaptersOpened"
            v-if="state.data"
            variant="other"
          />
          <LayoutsFooter />
        </article>
      </AppPageLoader>
    </div>
    <AppAnimatedTitle />
  </div>
</template>

<script setup lang="ts">

import { computed, onMounted, reactive } from 'vue'
import { TLongreadPage } from '~/types/page-types'
import { useBlock } from '#imports'
import { useChapterStore } from '~/store/chapters'
import formatDate from '~/utils/date'

const pageCollection = 'longread_page'
const chapterStore = useChapterStore()

type TState = {
  data?: TLongreadPage
  date?: string;
  update?: string;
};

const state = reactive<TState>({
  data: undefined,
  date: undefined,
  update: undefined
})

const onPageLoaded = (data) => {
  state.data = data
  state.date = formatDate(data.date_created)

  if (
    data.date_revisioned !== null &&
    data.date_revisioned !== data.date_created
  ) {
    state.update = `Update: ${formatDate(data.date_revisioned)}`
  }

  if (typeof window !== 'undefined') {
    window.scrollTo(0, 0)
  }

  const publishedDate = data?.date_created ? new Date(data?.date_created).toISOString().split('T')[0] : undefined
  const revisionDate = data?.date_revisioned ? new Date(data?.date_revisioned).toISOString().split('T')[0] : undefined

  useJsonld({
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: data.title,
    datePublished: publishedDate,
    dateModified: revisionDate,
    author: {
      '@type': 'Organization',
      name: 'AVROTROS'
    }
  })
}

const computedChaptersOpened = computed(() => {
  return chapterStore.getOpen(state.data?.id)
})

onMounted(() => {
  window.scrollTo(0, 0)
})

</script>

<style scoped lang="postcss">
.longread-detail-view {
  &__longread-hero {
    overflow: clip;
    @mixin app-show-hero;
  }

  &__longread-header {
    position: sticky;
    left: 0px;
    top: 0px;
    display: flex;
    width: 100%;
    @mixin app-max-header-height;
    @mixin app-show-content;
    background: linear-gradient(26deg, rgba(0, 0, 0, 0.70) 0%,
      rgba(0, 0, 0, 0.00) 55.96%);
  }

  &__inner {
    @mixin container;
    @mixin app-container-padding;
    @mixin app-grid;
  }

  &__content {
    grid-column: 1 / -1;
    display: flex;
    flex-direction: column;
    justify-content: flex-end;
    padding-bottom: 2rem;

    @media (--lg) {
      grid-column: span 10 / span 10;
      grid-column-start: 2;
    }

    @media (--xxl) {
      grid-column: span 8 / span 8;
      grid-column-start: 3;
    }
  }

  &__title {
    text-align: center;
    font-family: Midnight, sans-serif;
    font-size: 27px;
    line-height: 1.1;

    @media (--md) {
      font-size: 42px;
      line-height: 0.9;
    }

    @media (--xl) {
      font-size: 3.75rem;
      line-height: 0.9;
    }
  }

  &__article {
    position: relative;
    z-index: 10;
    background-color: var(--color-blue-prussian);
    @mixin app-show-content;
  }

  &__wrapper {
    @mixin container;
    padding-top: 1.5rem;
    @mixin app-container-padding;
    @mixin app-grid;

    @media (--xl) {
      padding-top: 2rem;
    }
  }

  &__container {
    grid-column: 1 / -1;

    @media (--lg) {
      grid-column: span 10 / span 10;
      grid-column-start: 2;
    }

    @media (--xxl) {
      grid-column: span 8 / span 8;
      grid-column-start: 3;
    }
  }

  &__small {
    margin-bottom: 0.5rem;
    display: block;
    font-size: 0.875rem;
    line-height: 1;
  }

  &__component {
    font-family: GraphikWide, sans-serif;
    font-size: 0.75rem;
    line-height: 1rem;
    font-weight: 700;
    text-transform: uppercase;
  }

  &__time {
    display: block;
    font-family: GraphikWide, sans-serif;
    font-size: 0.75rem;
    line-height: 1rem;
    font-weight: 700;
    text-transform: uppercase;
  }

  &__tag-button-list {
    margin-top: 2rem;
  }

  &__content-wrapper {
    overflow: clip;
    padding-top: 3rem;
  }

  &__longread-header, &__longread-hero {
    /* aspect ratio's same as img sizes in LongreadHero.vue */
    aspect-ratio: 575 / 874;

    @media (--sm) {
      aspect-ratio: 767 / 415;
    }

    @media (--md) {
      aspect-ratio: 991 / 953;
    }

    @media (--lg) {
      aspect-ratio: 1199 / 649;
    }
  }
}

</style>
